import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login } from '../services/api';
import { Phone, ArrowRight, Shield } from 'lucide-react';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Format phone number
      let formattedPhone = phoneNumber.replace(/\D/g, '');
      if (!formattedPhone.startsWith('1') && formattedPhone.length === 10) {
        formattedPhone = '1' + formattedPhone;
      }
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+' + formattedPhone;
      }

      const response = await login(formattedPhone);

      if (response.success) {
        navigate('/verify', { 
          state: { 
            phoneNumber: formattedPhone,
            isLogin: true 
          } 
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
      {/* Header with Logo */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="max-w-md mx-auto flex items-center justify-center">
          <img 
            src="/buggybusters_1.jpg" 
            alt="BuggyBusters" 
            className="h-14 w-auto"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          
          {/* Welcome Section */}
          <div className="text-center mb-8">
            <div className="w-48 h-48 mx-auto mb-4">
              <img 
                src="/illustrations/Welcome-rafiki.svg" 
                alt="Welcome back" 
                className="w-full h-full"
              />
            </div>
            <h1 className="text-2xl font-bold text-buggy-dark mb-2">Welcome Back</h1>
            <p className="text-buggy-gray">
              Enter your mobile number to receive a login code
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-semibold text-buggy-dark mb-2">
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Phone size={18} strokeWidth={2} className="text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="phoneNumber"
                  placeholder="+1 555 555 5555"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full pl-11 pr-4 py-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-buggy-blue focus:border-transparent"
                  required
                />
              </div>
              <p className="text-xs text-buggy-gray mt-2">
                We'll send a verification code to this number
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-buggy-blue text-white font-semibold py-4 px-6 rounded-lg hover:bg-buggy-blue-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
            >
              {loading ? 'Sending Code...' : (
                <>
                  Continue
                  <ArrowRight size={20} strokeWidth={2} />
                </>
              )}
            </button>
          </form>

          {/* Trust Badge */}
          <div className="mt-6 flex items-center justify-center gap-2 text-buggy-gray">
            <Shield size={16} strokeWidth={2} />
            <p className="text-xs">Secure login for BuggyBusters customers</p>
          </div>

          {/* Divider */}
          <div className="my-8 border-t border-gray-200"></div>

          {/* Signup Link */}
          <div className="text-center">
            <p className="text-sm text-buggy-gray mb-2">New to InsiderPass?</p>
            <button
              onClick={() => navigate('/signup')}
              className="text-buggy-blue hover:text-buggy-blue-light font-semibold text-base"
            >
              Create an account
            </button>
          </div>

          {/* Footer Links */}
          <div className="text-center text-sm text-buggy-gray mt-8">
            <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
            {' · '}
            <Link to="/terms" className="hover:underline">Terms of Service</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
